<script setup lang="ts">
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { toggleFullscreen } from '@@/bits/fullscreen'
import { __ } from '@@/bits/intl'
import type { AutoplayDurationType } from '@@/bits/slideshow'
import { SLIDESHOW_ANALYTICS_ID } from '@@/bits/slideshow'
import { defineAsyncComponent } from '@@/bits/vue'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import { AccessibilitySetting } from '@@/pinia/accessibility_settings_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useWindowSizeStore } from '@@/pinia/window_size'
import AccessibilitySettingsModal from '@@/vuecomponents/AccessibilitySettingsModal.vue'
import AutoplaySettings from '@@/vuecomponents/AutoplaySettings.vue'
import GlobalSnackbar from '@@/vuecomponents/GlobalSnackbar.vue'
import SurfaceSlideshowContent from '@@/vuecomponents/SurfaceSlideshowContent.vue'
import SurfaceSlideshowEmbedPanel from '@@/vuecomponents/SurfaceSlideshowEmbedPanel.vue'
import SurfaceSlideshowMoreMenu from '@@/vuecomponents/SurfaceSlideshowMoreMenu.vue'
import SurfaceSlideshowSharePanel from '@@/vuecomponents/SurfaceSlideshowSharePanel.vue'
import SurfaceSlideshowShareViaAnotherAppPanel from '@@/vuecomponents/SurfaceSlideshowShareViaAnotherAppPanel.vue'
import { useSlideshowAutoplay } from '@@/vuecomposables/slideshow_autoplay'
import { useSlideshowLoadingState } from '@@/vuecomposables/slideshow_loading_state'
import { useSlideshowMenus } from '@@/vuecomposables/slideshow_menus'
import { useSlideshowNavigation } from '@@/vuecomposables/slideshow_navigation'
import { useSlideshowNavigationVisibility } from '@@/vuecomposables/slideshow_navigation_visibility'
import { useSlideshowOverlays } from '@@/vuecomposables/slideshow_overlays'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { useSlideshowPanels } from '@@/vuecomposables/slideshow_panels'
import { useSlideshowQRCode } from '@@/vuecomposables/slideshow_qr_code'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance } from 'vue'
import { computed, ref } from 'vue'

const SurfaceSlideshowLanguagePanel = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSlideshowLanguagePanel.vue'),
)

const props = defineProps<{
  currentPageElement: HTMLDivElement | undefined
}>()

const {
  isSmallerThanTabletPortrait,
  isSmallerThanDesktop,
  isSmallerThanDesktop3XL,
  isFullscreen,
  windowWidth,
  windowHeight,
  safeAreaInsets,
} = storeToRefs(useWindowSizeStore())
const { dir, isScreenshotMode, isEmbedded } = storeToRefs(useSurfaceStore())
const { startingState } = storeToRefs(useSurfaceStartingStateStore())

const {
  onPrevious,
  onNext,
  goToPage,
  numberOfPages,
  pages,
  currentPageIndex,
  currentPageNumber,
  isFirstPage,
  isLastPage,
  coverPageUrl,
} = useSlideshowPages()
const { toggleQRCode, isQrCodeShown } = useSlideshowQRCode()
const { isMoreMenuOpen } = useSlideshowMenus()
const {
  isSharePanelOpen,
  isShareViaAnotherAppPanelOpen,
  isEmbedPanelOpen,
  isLanguagePanelOpen,
  isAccessibilitySettingsModalOpen,
} = useSlideshowPanels()
const { isAnyOverlayOpen, isAutoplayMenuOpen } = useSlideshowOverlays()
const {
  isAutoplayPaused,
  isAutoplaying,
  isAutoplayLooping,
  indicatorTransformScaleX,
  selectedAutoplayDuration,
  computedAutoplayDurationInMs,
  toggleAutoplay,
} = useSlideshowAutoplay()
const handleOnSelectAutoplayDurationType = (value: AutoplayDurationType) => {
  selectedAutoplayDuration.value = value
  isAutoplayMenuOpen.value = false
}

const containerRef = ref<HTMLDivElement | undefined>()
const moreMenuRef = ref<ComponentPublicInstance<HTMLElement> | null>(null)
const shareMenuRef = ref<ComponentPublicInstance<HTMLElement> | null>(null)
const moreMenuElement = computed(() => moreMenuRef.value?.$el)

useSlideshowNavigation({
  currentPageElement: props.currentPageElement,
  slideshowNavigationBarContainerRef: containerRef,
})

const { hasDarkBackgroundLuminance, hasLightBackgroundLuminance, primaryColorStyle } = useSlideshowStyles()

const BUTTON_COLOR_SCHEME = OzIconButtonColorScheme.SecondaryClear
const BUTTON_SIZE_PRESET = OzIconButtonSizePreset.H40px
const BUTTON_ICON_SIZE = 24
const BUTTON_DISABLED_TRANSPARENCY_CLASS = 'aria-disabled:!bg-transparent'
const slideshowStatusDescription = computed(() =>
  __('Current slide: %{currentPage} out of %{totalPages}', {
    currentPage: currentPageNumber.value,
    totalPages: numberOfPages.value,
  }),
)

// Indicator
const MINIMUM_PADDING_FROM_EDGE_OF_SCREEN_TO_PREVIEW = 12
const INDICATOR_BUTTON_PADDING_START = 2
const PREVIEW_BORDER_WIDTH = 6

// Used as padding to make sure the slide content looks accurate in the preview
// Since there won't be a navigation bar inside the preview but we still want to account
// for the same space that is usually taken by it.
const navigationBarHeight = ref(0)
/**
 * Set navbarHeight when the user mouse has entered / focused.
 * This is more reliable than setting it on mount which can sometimes read the wrong height.
 */
const setNavigationBarHeight = () => {
  navigationBarHeight.value = containerRef.value?.offsetHeight ?? 0
}
const indicatorButtonWidth = computed(() => windowWidth.value / numberOfPages.value)
const shouldIndicatorsBePaginated = computed(() => indicatorButtonWidth.value >= 20)

// Includes the width of the left and right borders
const previewContainerWidth = computed(() => {
  if (isSmallerThanTabletPortrait.value) {
    return 116
  }
  if (isSmallerThanDesktop.value) {
    return 224
  }
  if (isSmallerThanDesktop3XL.value) {
    return 396
  }
  return 612
})
// Set height according to aspect ratio of window so that preview looks exactly like the actual content on the slide
const previewContainerHeight = computed(() => (windowHeight.value / windowWidth.value) * previewContainerWidth.value)

const previewContentWidth = computed(() => previewContainerWidth.value - PREVIEW_BORDER_WIDTH * 2)
const previewContentHeight = computed(() => previewContainerHeight.value - PREVIEW_BORDER_WIDTH * 2)
const previewContentTransformScaleX = computed(() => previewContentWidth.value / windowWidth.value)
const previewContentTransformScaleY = computed(() => previewContentHeight.value / windowHeight.value)

const previewStartPosition = computed(() => {
  if (currentPreviewPageIndex.value == null) return 0
  const startPositionForIndicatorButton =
    indicatorButtonWidth.value * currentPreviewPageIndex.value + INDICATOR_BUTTON_PADDING_START

  // Formula: position1 = position2 + ((width2 - width1) / 2)
  const startPositionToCenterPreviewOverIndicatorButton =
    startPositionForIndicatorButton + (indicatorButtonWidth.value - previewContainerWidth.value) / 2

  // Preview position should be bounded by 12px from left or right edge of screen
  // (Equidistant padding from the indicator button)
  const endEdge = windowWidth.value - previewContainerWidth.value - MINIMUM_PADDING_FROM_EDGE_OF_SCREEN_TO_PREVIEW
  const previewStartPositionBoundedToStartEdge = Math.max(
    startPositionToCenterPreviewOverIndicatorButton,
    MINIMUM_PADDING_FROM_EDGE_OF_SCREEN_TO_PREVIEW,
  )
  const previewStartPositionBoundedToBothEdges = Math.min(previewStartPositionBoundedToStartEdge, endEdge)

  return previewStartPositionBoundedToBothEdges
})

const currentHoveredPageIndex = ref<number | null>(null)
const currentPreviewPageIndex = computed(() => currentHoveredPageIndex.value ?? currentPageIndex.value)
const isCurrentlyDragging = ref(false)
const shouldShowActiveButtonStyle = (indicatorButtonPageIndex: number) => {
  if (!isCurrentlyDragging.value) {
    return true
  }
  return indicatorButtonPageIndex === currentHoveredPageIndex.value
}
// On touch devices, active state isn't triggered while the user is dragging across the indicators
const shouldForceActiveButtonStyle = (indicatorButtonPageIndex: number) => {
  if (!device.touchable) {
    return false
  }
  return indicatorButtonPageIndex === currentHoveredPageIndex.value
}

const onIndicatorButtonPointerUp = (pageIndex: number) => {
  goToPage(pageIndex)
  currentHoveredPageIndex.value = null
  isCurrentlyDragging.value = false
  showNavigationBarWithTimeout()
}
const onIndicatorButtonPointerDown = (event: PointerEvent, pageIndex: number) => {
  // Pointer capture must be released otherwise subsequent pointer events on other buttons
  // will not trigger events and their event handlers (e.g. pointerenter, pointerup etc)
  // Reference: https://w3c.github.io/pointerevents/#releasing-pointer-capture
  const buttonElement = event?.target as Element
  buttonElement.releasePointerCapture(event.pointerId)
  currentHoveredPageIndex.value = pageIndex
  isCurrentlyDragging.value = true
}
const onIndicatorButtonPointerEnter = (pageIndex: number) => {
  currentHoveredPageIndex.value = pageIndex
}
const onIndicatorButtonPointerLeave = () => {
  currentHoveredPageIndex.value = null
}

const previousAndNextSlideControlsRef = ref<HTMLDivElement | undefined>()
const { hasLoadedPages } = useSlideshowLoadingState()
const {
  isInertWhileNavigationBarInvisible,
  isNavigationBarVisible,
  showNavigationBarWithTimeout,
  startTimeoutForHidingNavigationBar,
  startShowingNavigationBarOnFocus,
  classesToAutoHideElement,
} = useSlideshowNavigationVisibility({
  isCurrentlyDragging,
  previousAndNextSlideControlsRef,
  containerRef,
  hasLoadedPages,
  isAnyOverlayOpen,
  isScreenshotMode,
})

const openShareOverlay = () => {
  isSharePanelOpen.value = true
}

const openLanguagePanel = () => {
  isLanguagePanelOpen.value = true
}

const openSlideshowInANewTab = () => {
  window.open(coverPageUrl.value, '_blank')
}
</script>

<template>
  <div
    ref="containerRef"
    :style="{
      backgroundColor: primaryColorStyle,
      paddingTop: 'var(--safe-area-inset-top)',
      paddingBottom: 'var(--safe-area-inset-bottom)',
      paddingLeft: 'var(--safe-area-inset-left)',
      paddingRight: 'var(--safe-area-inset-right)',
    }"
    @focusin="startShowingNavigationBarOnFocus"
    @focusout="startTimeoutForHidingNavigationBar"
  >
    <div
      id="slideshow-nav-bar-content"
      :class="['w-full']"
      @mouseenter.once="setNavigationBarHeight"
      @touchstart.once="setNavigationBarHeight"
      @focusin.once="setNavigationBarHeight"
    >
      <!-- Indicator -->
      <div
        :inert="!isNavigationBarVisible"
        data-testid="slideshowNavigationBarIndicator"
        :class="[
          'w-full',
          'box-border',
          'flex',
          '-mb-3', // Needs to match indicator button padding bottom
          classesToAutoHideElement,
        ]"
        :aria-hidden="true"
      >
        <!-- INDICATOR PREVIEW -->

        <!-- Use inert to prevent click/focus events on the slideshow content -->
        <!-- The alternative would be recursively applying tabindex="-1" to all child nodes -->
        <!-- which is challenging especially because of the different attachment types -->
        <div
          v-if="currentHoveredPageIndex != null"
          inert
          :aria-hidden="true"
          :class="[
            'z-50', // Preview overlaps with the actual slide content, without this the preview would get blocked by the attachment loader
            'fixed', // Use fixed to position in initial containing block, would use Teleport if we used Vue 3
            'border-solid rounded-[18px]',
            'box-border',
            'overflow-hidden',
            'flex justify-center items-center',
            hasDarkBackgroundLuminance ? 'border-light-text-200' : 'border-dark-text-200 ',
          ]"
          :style="{
            backgroundColor: primaryColorStyle,
            bottom: `${navigationBarHeight}px`,
            insetInlineStart: `${previewStartPosition}px`,
            width: `${previewContainerWidth}px`,
            height: `${previewContainerHeight}px`,
            borderWidth: `${PREVIEW_BORDER_WIDTH}px`,
          }"
        >
          <!-- For slideshows with multiple long posts, the preloading is too expensive -->
          <!-- The speed at which slides advance is much faster for preview so there's too much layout thrashing. -->
          <!-- (That happens in the resize post composable) -->

          <!-- Also, there's diminishing returns for preloading because of the speed at which slides are moved through -->
          <!-- and the fact that users can (and most likely will) move through slides in a non-consecutive order. -->

          <!-- Starting with a conversative value of 0, because even 1 caused some lag on larger padlets. -->
          <SurfaceSlideshowContent
            v-if="currentPreviewPageIndex != null"
            :class="['shrink-0']"
            :data-testid="`slideshowNavigationBarIndicatorContentPreview-${currentPreviewPageIndex + 1}`"
            :pages="pages"
            :current-page-index="currentPreviewPageIndex"
            :overscan-page-limit="0"
            :style="{
              // Set the content to the exact dimensions of what the user sees
              // Then scale it down to fit inside the preview
              width: `${windowWidth}px`,
              height: `${windowHeight - navigationBarHeight}px`,
              transform: `scale(${previewContentTransformScaleX}, ${previewContentTransformScaleY})`,
              paddingBottom: `${navigationBarHeight}px`,
            }"
          />
        </div>

        <!-- INDICATOR BUTTONS -->
        <!-- Indicator buttons are not focusable because it worsens the UX for tab focus -->
        <!-- The user would need to tab through all the indicator buttons before reaching the controls -->

        <!-- pageIndicatorNumber starts from 1 -->
        <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
        <OzBaseButton
          v-for="pageIndicatorNumber in numberOfPages"
          :key="pageIndicatorNumber"
          :data-testid="`slideshowNavigationBarIndicatorButton-${pageIndicatorNumber}`"
          :data-pepin-id="`${SLIDESHOW_ANALYTICS_ID['slideshow-indicator-button']}-${pageIndicatorNumber}`"
          data-pepin-trigger="click"
          :class="[
            'group',
            'pb-3 pt-3.5 hhover:pt-3',
            shouldShowActiveButtonStyle(pageIndicatorNumber - 1) && 'hactive:pt-3',
            shouldForceActiveButtonStyle(pageIndicatorNumber - 1) && '!pt-3',
            'z-10', // The indicator button overlaps the navbar, make the bottom padding within the bar clickable
            'bg-clip-content',
            // Start/end padding should be included in part of the button which is why we don't add it to the parent container
            // It's better UX to avoid a dead area that can't be clicked by the user, especially when the indicator is narrow
            shouldIndicatorsBePaginated
              ? [
                  'px-0.5',
                  pageIndicatorNumber === 1 && 'ps-1 pe-0.5',
                  pageIndicatorNumber === numberOfPages && 'pe-1 ps-0.5',
                ]
              : ['px-0', pageIndicatorNumber === 1 && 'ps-1', pageIndicatorNumber === numberOfPages && 'pe-1'],
            'bg-transparent', // OzBaseButton override
          ]"
          :style="{
            width: `${
              pageIndicatorNumber === 1 || pageIndicatorNumber === numberOfPages
                ? indicatorButtonWidth + 2 // Account for the extra 2px for first/last slide button
                : indicatorButtonWidth
            }px`,
            // This is needed for pointer* event handlers to work correctly on touch devices
            touchAction: 'none',
          }"
          tabindex="-1"
          @pointerup="onIndicatorButtonPointerUp(pageIndicatorNumber - 1)"
          @pointerdown="onIndicatorButtonPointerDown($event, pageIndicatorNumber - 1)"
          @pointerenter="onIndicatorButtonPointerEnter(pageIndicatorNumber - 1)"
          @pointerleave="onIndicatorButtonPointerLeave"
        >
          <!-- Don't use hover-hover: for active styles because we want it to appear on mobile too -->
          <!-- And there's currently no hactive equivalent for group -->
          <div
            :class="[
              'relative',
              'h-1.5 hover-hover:group-hover:h-2',
              shouldShowActiveButtonStyle(pageIndicatorNumber - 1) && 'group-active:h-2',
              shouldForceActiveButtonStyle(pageIndicatorNumber - 1) && '!h-2',
              hasDarkBackgroundLuminance ? 'bg-light-text-400' : 'bg-dark-text-400',
              'w-full',
              shouldIndicatorsBePaginated && 'rounded-xs',
            ]"
          >
            <!-- Hovered indicator button overlay -->
            <div
              :class="[
                'invisible hover-hover:group-hover:visible',
                shouldShowActiveButtonStyle(pageIndicatorNumber - 1) && 'group-active:visible',
                shouldForceActiveButtonStyle(pageIndicatorNumber - 1) && '!visible',
                hasDarkBackgroundLuminance ? 'bg-light-text-300' : 'bg-dark-text-300',
                'absolute',
                'h-2 w-full',
                shouldIndicatorsBePaginated && 'rounded-xs',
              ]"
            />
            <!-- Past slides / Current slide indicator button overlay -->
            <div
              v-if="currentPageNumber != null && currentPageNumber >= pageIndicatorNumber"
              :class="[
                'hover-hover:group-hover:invisible',
                shouldShowActiveButtonStyle(pageIndicatorNumber - 1) && 'group-active:invisible',
                shouldForceActiveButtonStyle(pageIndicatorNumber - 1) && '!invisible',
                hasDarkBackgroundLuminance ? 'bg-light-text-400' : 'bg-dark-text-400',
                'absolute',
                'h-1.5 w-full',
                shouldIndicatorsBePaginated && 'rounded-xs',
                dir === 'ltr' ? 'origin-left' : 'origin-right',
              ]"
              :style="{
                // Animate the bar from left to right when autoplay is enabled
                // Based on the duration selected
                transform:
                  ((isAnyOverlayOpen && isAutoplayPaused) || isAutoplaying) && currentPageNumber === pageIndicatorNumber
                    ? `scaleX(${indicatorTransformScaleX})`
                    : undefined,
              }"
            />
          </div>
        </OzBaseButton>
      </div>

      <!-- Navigation control buttons -->
      <div
        :class="[
          'box-border',
          'desktop:gap-2 grid grid-cols-3',
          'w-full',
          'px-2',
          'tablet-portrait:px-4',
          device.app ? 'py-2' : 'py-3',
          'font-sans',
        ]"
      >
        <!-- Autoplay controls -->
        <div
          :class="['flex flex-row items-center gap-2', classesToAutoHideElement]"
          data-testid="slideshowNavigationBarAutoplayControls"
          :inert="isInertWhileNavigationBarInvisible"
        >
          <OzIconButton
            :class="BUTTON_DISABLED_TRANSPARENCY_CLASS"
            :dark-mode="hasDarkBackgroundLuminance"
            :color-scheme="BUTTON_COLOR_SCHEME"
            :icon-name="isAutoplaying ? 'pause_outline' : 'play_outline'"
            :icon-size="BUTTON_ICON_SIZE"
            :size-preset="BUTTON_SIZE_PRESET"
            :label="isAutoplaying ? __('Pause autoplay') : __('Start autoplay')"
            :data-pepin-id="SLIDESHOW_ANALYTICS_ID['slideshow-autoplay-button']"
            data-pepin-trigger="click"
            data-testid="slideshowNavigationBarAutoplayButton"
            @click="toggleAutoplay"
          />

          <AutoplaySettings
            :x-autoplay-menu="isAutoplayMenuOpen"
            :is-autoplay-looping="isAutoplayLooping"
            :selected-autoplay-duration="selectedAutoplayDuration"
            :autoplay-duration-in-ms="computedAutoplayDurationInMs"
            :dark-mode="hasDarkBackgroundLuminance"
            :test-id-prefix="'slideshowNavigationBar'"
            :duration-type-analytics-id-prefix="SLIDESHOW_ANALYTICS_ID['slideshow-autoplay-duration-button']"
            :loop-analytics-id="SLIDESHOW_ANALYTICS_ID['slideshow-autoplay-loop-slides-button']"
            @toggle-autoplay-menu="isAutoplayMenuOpen = !isAutoplayMenuOpen"
            @toggle-loop="isAutoplayLooping = !isAutoplayLooping"
            @select-duration-type="handleOnSelectAutoplayDurationType"
          />
        </div>

        <!-- Previous/next slide controls -->

        <!-- Touchable device: Always show entire controls -->
        <!-- Screenshot mode: Always show slide numbers -->
        <div
          ref="previousAndNextSlideControlsRef"
          data-testid="slideshowNavigationBarPreviousAndNextSlideControls"
          :class="[
            'flex items-center justify-between gap-4 col-start-2 place-self-center',
            // It's not possible to override opacity-0 on a child so we don't apply the class if it needs to be always shown
            !device.touchable && !isScreenshotMode && classesToAutoHideElement,
          ]"
        >
          <OzIconButton
            :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS, isScreenshotMode && classesToAutoHideElement]"
            :dark-mode="hasDarkBackgroundLuminance"
            :color-scheme="BUTTON_COLOR_SCHEME"
            :icon-name="'chevron_left_center'"
            :icon-size="BUTTON_ICON_SIZE"
            :size-preset="BUTTON_SIZE_PRESET"
            :label="__('Previous slide')"
            :disabled="isFirstPage"
            :data-pepin-id="SLIDESHOW_ANALYTICS_ID['presentation-view-previous-button']"
            data-pepin-trigger="click"
            :data-testid="'slideshowNavigationBarButtonPreviousSlide'"
            @click="() => onPrevious(SLIDESHOW_ANALYTICS_ID['presentation-view-previous-button'])"
          />
          <div :class="['text-center text-body whitespace-nowrap']" aria-hidden="true">
            <span
              :class="{
                'text-dark-text-100': hasLightBackgroundLuminance,
                'text-light-text-100': hasDarkBackgroundLuminance,
              }"
              :data-testid="'slideshowNavigationBarCurrentPageNumber'"
              :style="{ width: `${numberOfPages.toString().length}ch`, display: 'inline-block' }"
            >
              {{ currentPageNumber }}
            </span>
            <span
              :class="{
                'text-dark-text-300': hasLightBackgroundLuminance,
                'text-light-text-300': hasDarkBackgroundLuminance,
              }"
            >
              /{{ ' ' }}
            </span>
            <span
              :class="{
                'text-dark-text-300': hasLightBackgroundLuminance,
                'text-light-text-300': hasDarkBackgroundLuminance,
              }"
              :data-testid="'slideshowNavigationBarNumberOfPages'"
            >
              {{ numberOfPages }}
            </span>
          </div>
          <p v-if="currentPageNumber && numberOfPages" class="sr-only" aria-live="polite">
            {{ slideshowStatusDescription }}
          </p>
          <OzIconButton
            :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS, isScreenshotMode && classesToAutoHideElement]"
            :dark-mode="hasDarkBackgroundLuminance"
            :color-scheme="BUTTON_COLOR_SCHEME"
            :icon-name="'chevron_right_center'"
            :icon-size="BUTTON_ICON_SIZE"
            :size-preset="BUTTON_SIZE_PRESET"
            :label="__('Next slide')"
            :disabled="isLastPage"
            :data-pepin-id="SLIDESHOW_ANALYTICS_ID['presentation-view-next-button']"
            data-pepin-trigger="click"
            :data-testid="'slideshowNavigationBarButtonNextSlide'"
            @click="() => onNext(SLIDESHOW_ANALYTICS_ID['presentation-view-next-button'])"
          />
        </div>

        <!-- More controls -->
        <div
          data-testid="slideshowNavigationBarMoreControls"
          :class="['place-self-end flex flex-row gap-2', classesToAutoHideElement]"
          :inert="isInertWhileNavigationBarInvisible"
        >
          <template v-if="isEmbedded">
            <OzIconButton
              ref="shareMenuRef"
              :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
              :dark-mode="hasDarkBackgroundLuminance"
              :color-scheme="BUTTON_COLOR_SCHEME"
              :icon-name="'share_arrow_outline'"
              :icon-size="BUTTON_ICON_SIZE"
              :size-preset="BUTTON_SIZE_PRESET"
              :label="__('Open share menu')"
              :data-pepin-id="SLIDESHOW_ANALYTICS_ID['slideshow-share-button']"
              data-pepin-trigger="click"
              data-testid="slideshowNavigationBarShareMenuButton"
              :disabled="!startingState.canIAccessContentDirectly"
              @click="openShareOverlay"
            />
            <OzIconButton
              :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
              :dark-mode="hasDarkBackgroundLuminance"
              :color-scheme="BUTTON_COLOR_SCHEME"
              :icon-name="'external_link'"
              :icon-size="BUTTON_ICON_SIZE"
              :size-preset="BUTTON_SIZE_PRESET"
              :label="__('Open in a new tab')"
              :data-pepin-id="SLIDESHOW_ANALYTICS_ID['slideshow-open-in-new-tab-button']"
              data-pepin-trigger="click"
              @click="openSlideshowInANewTab"
            />
            <OzIconButton
              ref="moreMenuRef"
              :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
              :dark-mode="hasDarkBackgroundLuminance"
              :color-scheme="BUTTON_COLOR_SCHEME"
              :icon-name="'dot_3_horizontal'"
              :icon-size="BUTTON_ICON_SIZE"
              :size-preset="BUTTON_SIZE_PRESET"
              :label="__('Open more menu')"
              :data-pepin-id="SLIDESHOW_ANALYTICS_ID['presentation-view-more-button']"
              data-pepin-trigger="click"
              data-testid="slideshowNavigationBarMoreMenuButton"
              @click="isMoreMenuOpen = true"
            />
          </template>
          <template v-else>
            <OzIconButton
              v-if="isFirstPage"
              data-pepin-trigger="click"
              :data-pepin-id="SLIDESHOW_ANALYTICS_ID['slideshow-qr-button']"
              :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
              :dark-mode="hasDarkBackgroundLuminance"
              :color-scheme="isQrCodeShown ? OzIconButtonColorScheme.SecondaryClearActive : BUTTON_COLOR_SCHEME"
              :icon-name="'qr'"
              :icon-size="BUTTON_ICON_SIZE"
              :size-preset="BUTTON_SIZE_PRESET"
              :label="__('Show QR code')"
              data-testid="slideshowNavigationBarQrCodeButton"
              @click="toggleQRCode"
            />

            <OzIconButton
              v-if="!(device.app || isSmallerThanTabletPortrait)"
              :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
              :dark-mode="hasDarkBackgroundLuminance"
              :color-scheme="BUTTON_COLOR_SCHEME"
              :icon-name="'accessibility_outline'"
              :icon-size="BUTTON_ICON_SIZE"
              :size-preset="BUTTON_SIZE_PRESET"
              :label="__('Accessibility settings')"
              :data-pepin-id="SLIDESHOW_ANALYTICS_ID['slideshow-accessibility-settings-button']"
              data-pepin-trigger="click"
              data-testid="slideshowNavigationBarAccessibilityButton"
              aria-haspopup="dialog"
              @click="isAccessibilitySettingsModalOpen = true"
            />

            <OzIconButton
              v-if="device.app || isSmallerThanTabletPortrait"
              ref="moreMenuRef"
              :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
              :dark-mode="hasDarkBackgroundLuminance"
              :color-scheme="BUTTON_COLOR_SCHEME"
              :icon-name="'dot_3_horizontal'"
              :icon-size="BUTTON_ICON_SIZE"
              :size-preset="BUTTON_SIZE_PRESET"
              :label="__('Open more menu')"
              :data-pepin-id="SLIDESHOW_ANALYTICS_ID['presentation-view-more-button']"
              data-pepin-trigger="click"
              data-testid="slideshowNavigationBarMoreMenuButton"
              @click="isMoreMenuOpen = true"
            />
            <template v-else>
              <OzIconButton
                v-if="isAppUsing('translatePadlet')"
                :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
                :dark-mode="hasDarkBackgroundLuminance"
                :color-scheme="BUTTON_COLOR_SCHEME"
                :icon-name="'globe'"
                :icon-size="BUTTON_ICON_SIZE"
                :size-preset="BUTTON_SIZE_PRESET"
                :label="__('Open language menu')"
                :data-pepin-id="SLIDESHOW_ANALYTICS_ID['slideshow-language-button']"
                data-pepin-trigger="click"
                data-testid="slideshowNavigationBarLanguageMenuButton"
                @click="openLanguagePanel"
              />
              <OzIconButton
                ref="shareMenuRef"
                :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
                :dark-mode="hasDarkBackgroundLuminance"
                :color-scheme="BUTTON_COLOR_SCHEME"
                :icon-name="'share_arrow_outline'"
                :icon-size="BUTTON_ICON_SIZE"
                :size-preset="BUTTON_SIZE_PRESET"
                :label="__('Open share menu')"
                :data-pepin-id="SLIDESHOW_ANALYTICS_ID['slideshow-share-button']"
                data-pepin-trigger="click"
                data-testid="slideshowNavigationBarShareMenuButton"
                :disabled="!startingState.canIAccessContentDirectly"
                @click="openShareOverlay"
              />
              <OzIconButton
                :class="[BUTTON_DISABLED_TRANSPARENCY_CLASS]"
                :dark-mode="hasDarkBackgroundLuminance"
                :color-scheme="BUTTON_COLOR_SCHEME"
                :icon-name="isFullscreen ? 'fullscreen_exit' : 'fullscreen'"
                :icon-size="BUTTON_ICON_SIZE"
                :size-preset="BUTTON_SIZE_PRESET"
                :label="isFullscreen ? __('Exit fullscreen') : __('Enter fullscreen')"
                :data-pepin-id="
                  isFullscreen
                    ? SLIDESHOW_ANALYTICS_ID['slideshow-exit-fullscreen-button']
                    : SLIDESHOW_ANALYTICS_ID['slideshow-enter-fullscreen-button']
                "
                data-pepin-trigger="click"
                data-testid="slideshowNavigationBarFullscreenButton"
                @click="toggleFullscreen"
              />
            </template>
          </template>
        </div>

        <!-- Panels -->
        <SurfaceSlideshowSharePanel v-if="isSharePanelOpen" />
        <SurfaceSlideshowShareViaAnotherAppPanel v-if="isShareViaAnotherAppPanelOpen" />
        <SurfaceSlideshowEmbedPanel v-if="isEmbedPanelOpen" />
        <SurfaceSlideshowLanguagePanel v-if="isLanguagePanelOpen" />
        <AccessibilitySettingsModal
          :visible="isAccessibilitySettingsModalOpen"
          :portal-selector="'body'"
          :settings-to-show="[
            AccessibilitySetting.KeyboardShortcuts,
            AccessibilitySetting.HighContrastMode,
            AccessibilitySetting.ReducedMotion,
          ]"
          :dark-mode="hasDarkBackgroundLuminance"
          @close="isAccessibilitySettingsModalOpen = false"
        />

        <!-- Menus -->
        <SurfaceSlideshowMoreMenu v-if="isMoreMenuOpen" :anchor-element="moreMenuElement" />
      </div>
    </div>

    <!-- Snackbar -->
    <GlobalSnackbar :dark-mode="hasDarkBackgroundLuminance" :top-offset="safeAreaInsets.top" />
  </div>
</template>

<style lang="postcss" scoped>
.slideshow-nav-bar-transition {
  transition: opacity 0.3s ease-in-out;
  -webkit-transition: opacity 0.3s ease-in-out;
}

@media (hover: hover) {
  #slideshow-nav-bar-content:not(:hover) .slideshow-nav-bar-hidden {
    opacity: 0;
  }
}

@media (hover: none) {
  .slideshow-nav-bar-hidden {
    opacity: 0;
  }
}
</style>
